import React, { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Typography, Alert, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useWidgetData } from "../../hooks/useWidgets";
import WidgetHeader from "./WidgetHeader";
import PlayerNameCell from "../PlayerNameCell";
import { buildWidgetTableRows } from "../../utils/widgetTableRows";
import {
  NAME_SORT_ORDER,
  STAT_SORT_ORDER,
  formatGridStatValue,
  getResultsGridSx,
  sortedClassFor,
} from "../../utils/resultsGrid";

const TableWidget = ({ config, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useWidgetData(
    config.dataSource,
    config.timeRange
  );
  const [sortModel, setSortModel] = useState([
    { field: "value", sort: "desc" },
  ]);
  const sortedField = sortModel[0]?.field;

  const handlePlayerClick = (playerId) => {
    navigate(`/app/player/${playerId}`);
  };

  const rows = useMemo(() => {
    if (config.dataSource !== "leaderboard") return [];
    return buildWidgetTableRows(
      data?.topPlayers || [],
      config.settings?.maxRows || 10
    );
  }, [config.dataSource, config.settings?.maxRows, data]);

  const columns = useMemo(
    () => [
      {
        field: "rank",
        headerName: "#",
        width: 56,
        sortable: false,
        align: "right",
        headerAlign: "right",
      },
      {
        field: "playerName",
        headerName: "Player",
        flex: 1.2,
        minWidth: 160,
        headerClassName: sortedClassFor("playerName", sortedField),
        cellClassName: sortedClassFor("playerName", sortedField),
        sortingOrder: NAME_SORT_ORDER,
        renderCell: (params) => (
          <PlayerNameCell
            playerId={params.row.playerId}
            name={params.value}
            onPlayerClick={handlePlayerClick}
          />
        ),
      },
      {
        field: "value",
        headerName: "Home Runs",
        width: 120,
        type: "number",
        align: "right",
        headerAlign: "right",
        headerClassName: sortedClassFor("value", sortedField),
        cellClassName: sortedClassFor("value", sortedField),
        sortingOrder: STAT_SORT_ORDER,
        renderCell: (params) => formatGridStatValue(params.value, "count"),
      },
    ],
    [sortedField]
  );

  const unsupportedSource =
    !isLoading && !error && config.dataSource !== "leaderboard";

  return (
    <Box
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderRadius: 4,
        border: 1,
        borderColor: "divider",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <WidgetHeader
        title={config.title}
        onEdit={onEdit ? () => onEdit(config) : null}
        onDelete={onDelete ? () => onDelete(config) : null}
        showDragHandle={Boolean(onEdit || onDelete)}
      />

      <Box flex={1} display="flex" flexDirection="column" minHeight={0}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : unsupportedSource ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flex={1}
          >
            <Typography color="text.secondary">
              No table data available for {config.dataSource}
            </Typography>
          </Box>
        ) : (
          <Paper sx={{ overflow: "hidden", flex: 1, minHeight: 0 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={isLoading}
              disableRowSelectionOnClick
              disableColumnMenu
              hideFooter
              sortingMode="client"
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              rowHeight={52}
              sx={getResultsGridSx("100%")}
            />
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default TableWidget;
